import { useEffect, useState, useMemo, useRef } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { getProveedores } from '../../lib/stockApi'
import {
  parseXmlCfe, getProveedorPorRut, sugerirProductos,
  crearCompraDesdeXml, crearCompraManual,
  listarCompras, marcarComoPagada, eliminarCompra,
  compraACsv, descargarCsv,
} from '../../lib/comprasApi'

const FORMAS_PAGO = ['Contado', 'Transferencia', 'Débito', 'Crédito', 'Cheque']
const CUENTAS_CONTABLES = [
  'Mercaderías',
  'Materiales y Suministros',
  'Combustible',
  'Mantenimiento Vehículos',
  'Servicios (luz, agua, internet)',
  'Marketing',
  'Honorarios',
  'Packaging',
  'Otros gastos',
]
const CENTROS_COSTO = ['Operativo', 'Vehículos', 'Marketing', 'Administración', 'Otros']

const TIPO_CFE_OPTIONS = [
  { v: 101, label: 'e-Ticket' },
  { v: 111, label: 'e-Factura' },
  { v: 102, label: 'NC e-Ticket' },
  { v: 112, label: 'NC e-Factura' },
]

export default function Compras() {
  const [vista, setVista] = useState('listado') // 'listado' | 'nueva-xml' | 'nueva-manual'
  const [compras, setCompras] = useState([])
  const [proveedores, setProveedores] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroProveedor, setFiltroProveedor] = useState('')
  const [filtroEstado, setFiltroEstado] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [sincronizando, setSincronizando] = useState(false)
  const [resultadoSync, setResultadoSync] = useState(null)

  useEffect(() => {
    Promise.all([listarCompras(), getProveedores()]).then(([c, p]) => {
      setCompras(c); setProveedores(p); setLoading(false)
    })
  }, [])

  async function recargar() {
    setLoading(true)
    const [c] = await Promise.all([listarCompras()])
    setCompras(c); setLoading(false)
  }

  const filtradas = useMemo(() => {
    let f = compras
    if (filtroProveedor) f = f.filter(c => c.proveedor?.id === filtroProveedor)
    if (filtroEstado) f = f.filter(c => c.estado === filtroEstado)
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      f = f.filter(c =>
        (c.emisor_razon_social || '').toLowerCase().includes(q) ||
        (c.serie + '-' + c.numero).toLowerCase().includes(q) ||
        (c.emisor_rut || '').includes(q)
      )
    }
    return f
  }, [compras, filtroProveedor, filtroEstado, busqueda])

  // Stats
  const stats = useMemo(() => {
    const totalMes = filtradas.reduce((s, c) => s + Number(c.monto_total || 0), 0)
    const ivaMes = filtradas.reduce((s, c) =>
      s + Number(c.monto_iva_basica || 0) + Number(c.monto_iva_minima || 0), 0)
    const pendientes = filtradas.filter(c => c.estado === 'pendiente').length
    return { total: filtradas.length, totalMes, ivaMes, pendientes }
  }, [filtradas])

  function exportarCsv() {
    const csv = compraACsv(filtradas)
    const fecha = new Date().toISOString().slice(0, 10)
    descargarCsv(`compras_limpack_${fecha}.csv`, csv)
  }

  async function sincronizarBiller() {
    setSincronizando(true)
    setResultadoSync(null)
    try {
      // Por defecto: últimos 30 días. Si querés más, pasamos fecha_desde manualmente.
      const { data, error } = await supabase.functions.invoke('biller-sync-recibidos', { body: {} })
      if (error) throw new Error(error.message)
      if (data?.error) throw new Error(data.error)
      setResultadoSync(data)
      // Si creó o actualizó algo, recargar la tabla
      if ((data?.creados || 0) > 0 || (data?.actualizados || 0) > 0) {
        await recargar()
      }
    } catch (e) {
      setResultadoSync({ error: e.message || String(e) })
    } finally {
      setSincronizando(false)
    }
  }

  return (
    <AdminLayout>
      <div className="page">
        <h1>Compras · Facturas Recibidas</h1>
        <p className="lead">e-Facturas recibidas de proveedores · Carga automática vía XML CFE · Export para Zeta</p>
        {/* Tabs */}
        <div className="btn-row" style={{ marginTop: 18 }}>
          <button
            className={`btn ${vista === 'listado' ? 'primary' : 'ghost'}`}
            onClick={() => setVista('listado')}>📋 Listado ({compras.length})</button>
          <button
            className={`btn ${vista === 'nueva-xml' ? 'primary' : 'ghost'}`}
            onClick={() => setVista('nueva-xml')}>📥 Cargar XML/PDF</button>
          <button
            className={`btn ${vista === 'nueva-manual' ? 'primary' : 'ghost'}`}
            onClick={() => setVista('nueva-manual')}>✏️ Manual</button>
          {vista === 'listado' && (
            <button className="btn ghost" onClick={exportarCsv}>📊 Export CSV (Zeta)</button>
          )}
          {vista === 'listado' && (
            <button
              className="btn ghost" onClick={sincronizarBiller} disabled={sincronizando}
              title="Trae los CFE recibidos de los últimos 30 días desde Biller y los carga acá"
              style={{ marginLeft: 'auto', borderColor: '#00A8B5', color: '#0e7490' }}>
              {sincronizando ? '⏳ Sincronizando…' : '🔄 Sincronizar desde Biller'}
            </button>
          )}
        </div>

        {resultadoSync && (
          <div style={{ padding: 12, marginTop: 10, borderRadius: 8,
            background: resultadoSync.error ? '#fee2e2' : '#dcfce7',
            border: `1px solid ${resultadoSync.error ? '#fca5a5' : '#86efac'}`,
            color: resultadoSync.error ? '#991b1b' : '#166534',
            fontSize: 13 }}>
            {resultadoSync.error
              ? <>❌ Error: {resultadoSync.error}</>
              : (
                <>
                  ✓ Sincronización completa. Período {resultadoSync.periodo?.fecha_desde} → {resultadoSync.periodo?.fecha_hasta} ·
                  Recibidos: <strong>{resultadoSync.total_recibidos}</strong> ·
                  Creados: <strong>{resultadoSync.creados}</strong> ·
                  Actualizados: <strong>{resultadoSync.actualizados}</strong>
                  {resultadoSync.errores?.length > 0 && <> · Con error: <strong>{resultadoSync.errores.length}</strong></>}
                </>
              )}
          </div>
        )}

        {vista === 'listado' && (
          <div style={{ padding: 10, background: '#f0f9ff', border: '1px solid #93c5fd', borderRadius: 8, marginTop: 12, marginBottom: 10, fontSize: 12, color: '#1e40af', lineHeight: 1.6 }}>
            <strong>📋 Flujo de una compra:</strong><br/>
            <span style={{ background: '#dcfce7', color: '#166534', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>① Aprobar</span> = reconozco la deuda en contabilidad (genera asiento DEBE Gasto+IVA / HABER Acreedores). <br/>
            <span style={{ background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>② Marcar pagada</span> = confirmo que ya pagué al proveedor (registra fecha + forma de pago). <br/>
            <span style={{ background: '#fee2e2', color: '#991b1b', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>↩ NC</span> = nota de crédito, no se paga (anula factura). Aparece restando en rojo.
          </div>
        )}

        {vista === 'listado' && (
          <ListadoCompras
            compras={filtradas}
            stats={stats}
            proveedores={proveedores}
            loading={loading}
            filtroProveedor={filtroProveedor}
            setFiltroProveedor={setFiltroProveedor}
            filtroEstado={filtroEstado}
            setFiltroEstado={setFiltroEstado}
            busqueda={busqueda}
            setBusqueda={setBusqueda}
            onRecargar={recargar}
          />
        )}

        {vista === 'nueva-xml' && (
          <CargaXml
            proveedores={proveedores}
            onSaved={() => { recargar(); setVista('listado') }}
          />
        )}

        {vista === 'nueva-manual' && (
          <CargaManual
            proveedores={proveedores}
            onSaved={() => { recargar(); setVista('listado') }}
          />
        )}
      </div>
    </AdminLayout>
  )
}

// ============================================================
// LISTADO
// ============================================================
function ListadoCompras({ compras, stats, proveedores, loading, filtroProveedor, setFiltroProveedor, filtroEstado, setFiltroEstado, busqueda, setBusqueda, onRecargar }) {
  const [previewCompra, setPreviewCompra] = useState(null)
  return (
    <>
      {/* KPIs */}
      <div className="cards-grid" style={{ marginTop: 18 }}>
        <div className="card">
          <h3>Compras filtradas</h3>
          <div className="stat">{stats.total}</div>
        </div>
        <div className="card">
          <h3>Total c/IVA</h3>
          <div className="stat">${stats.totalMes.toLocaleString('es-UY', { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="card">
          <h3>IVA crédito</h3>
          <div className="stat" style={{ color: '#0891B2' }}>${stats.ivaMes.toLocaleString('es-UY', { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="card" style={{ borderLeft: stats.pendientes > 0 ? '4px solid #F59E0B' : undefined }}>
          <h3>Pendientes pago</h3>
          <div className="stat" style={{ color: stats.pendientes > 0 ? '#F59E0B' : undefined }}>{stats.pendientes}</div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(200px, 1fr) 200px 180px',
        gap: 10, marginTop: 18, marginBottom: 12,
      }}>
        <input type="search"
          placeholder="🔎 Buscar razón social, RUT o N° factura..."
          value={busqueda} onChange={e => setBusqueda(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: 13 }} />
        <select value={filtroProveedor} onChange={e => setFiltroProveedor(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: 13, background: '#fff' }}>
          <option value="">Todos los proveedores</option>
          {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
        </select>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid #E2E8F0', fontSize: 13, background: '#fff' }}>
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendientes</option>
          <option value="pagada">Pagadas</option>
          <option value="parcial">Parcial</option>
          <option value="anulada">Anuladas</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">Cargando…</div>
      ) : compras.length === 0 ? (
        <div className="empty" style={{ textAlign: 'center', padding: 60, color: '#64748B' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📥</div>
          <h3>Aún no hay compras cargadas</h3>
          <p>Cargá una factura arrastrando el XML CFE o cargándola manual.</p>
        </div>
      ) : (
        <div style={{ marginTop: 8, background: '#fff', borderRadius: 10, overflow: 'hidden', border: '1px solid #E2E8F0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#F8FAFC' }}>
              <tr>
                <th style={th}>Fecha</th>
                <th style={th}>Proveedor</th>
                <th style={th}>Comprobante</th>
                <th style={{ ...th, textAlign: 'right' }}>Neto</th>
                <th style={{ ...th, textAlign: 'right' }}>IVA</th>
                <th style={{ ...th, textAlign: 'right' }}>Total</th>
                <th style={{ ...th, textAlign: 'center' }}>Estado</th>
                <th style={{ ...th, textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {compras.map(c => (
                <tr key={c.id} style={{ borderTop: '1px solid #F1F5F9' }}>
                  <td style={td}>{new Date(c.fecha_emision + 'T12:00:00').toLocaleDateString('es-UY')}</td>
                  <td style={td}>
                    {(c.proveedor?.nombre || c.emisor_razon_social) ? (
                      <div style={{ fontWeight: 600 }}>{c.proveedor?.nombre || c.emisor_razon_social}</div>
                    ) : (
                      <div style={{ fontWeight: 600, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
                        ⚠ Sin proveedor
                        <a
                          href={`/admin/proveedores?nuevo=1&rut=${encodeURIComponent(c.emisor_rut || '')}`}
                          style={{ fontSize: 10, padding: '2px 8px', background: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b', borderRadius: 4, fontWeight: 700, textDecoration: 'none' }}
                          title="Registrar este RUT como proveedor para que el próximo sync lo asocie">
                          + Crear
                        </a>
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: '#64748B' }}>RUT {c.emisor_rut}</div>
                  </td>
                  <td style={td}>
                    {[102, 112, 122, 132, 142, 152].includes(c.tipo_cfe) ? (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span style={{ fontSize: 10, padding: '1px 6px', background: '#fee2e2', color: '#991b1b', borderRadius: 4, fontWeight: 700 }}>↩ NC</span>
                          <strong style={{ color: '#dc2626' }}>{c.serie}-{c.numero}</strong>
                        </div>
                        <div style={{ fontSize: 10, color: '#dc2626' }}>{tipoCfeLabel(c.tipo_cfe)} (anula deuda)</div>
                      </>
                    ) : (
                      <>
                        <div>{c.serie}-{c.numero}</div>
                        <div style={{ fontSize: 10, color: '#94A3B8' }}>{tipoCfeLabel(c.tipo_cfe)}</div>
                      </>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: [102,112,122,132,142,152].includes(c.tipo_cfe) ? '#dc2626' : 'inherit' }}>
                    {[102,112,122,132,142,152].includes(c.tipo_cfe) ? '−' : ''}${Number(c.monto_neto).toLocaleString('es-UY')}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: [102,112,122,132,142,152].includes(c.tipo_cfe) ? '#dc2626' : '#0891B2' }}>
                    {[102,112,122,132,142,152].includes(c.tipo_cfe) ? '−' : ''}${(Number(c.monto_iva_basica) + Number(c.monto_iva_minima)).toLocaleString('es-UY')}
                  </td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: [102,112,122,132,142,152].includes(c.tipo_cfe) ? '#dc2626' : '#1A3461' }}>
                    {[102,112,122,132,142,152].includes(c.tipo_cfe) ? '−' : ''}${Number(c.monto_total).toLocaleString('es-UY')}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    <BadgeEstado estado={c.estado} />
                  </td>
                  <td style={{ ...td, textAlign: 'center', verticalAlign: 'middle' }}>
                    <MenuAcciones compra={c} onPreview={() => setPreviewCompra(c)} onRecargar={onRecargar} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Preview Asiento */}
      {previewCompra && (
        <ModalPreviewAsiento
          compra={previewCompra}
          onClose={() => setPreviewCompra(null)}
          onAprobado={() => { setPreviewCompra(null); onRecargar() }}
        />
      )}
    </>
  )
}

// ============================================================
// MODAL EDITOR ASIENTO — permite editar cuentas, montos, agregar/quitar líneas
// ============================================================
function ModalPreviewAsiento({ compra, onClose, onAprobado }) {
  const storageKey = `compra-asiento-borrador-${compra.id}`
  const [decidido, setDecidido] = useState(false)  // hasta decidir restaurar/descartar, no auto-guardamos

  const [cuenta, setCuenta] = useState(compra.cuenta_contable || 'Servicios (luz, agua, internet)')
  const [aprobando, setAprobando] = useState(false)
  const [planCuentas, setPlanCuentas] = useState([])
  const [autoSaved, setAutoSaved] = useState(null)

  const esNC = [102, 112, 122, 132, 142, 152].includes(compra.tipo_cfe)
  const signo = esNC ? -1 : 1
  const proveedor = compra.emisor_razon_social || `RUT ${compra.emisor_rut}`

  // Sugerir cuenta gasto según texto
  function cuentaGastoCodigo(txt) {
    const t = (txt || '').toLowerCase()
    if (t.includes('mercader') || t.includes('packaging') || t.includes('material')) return '115101'
    return '511204'
  }

  // Líneas iniciales: sugeridas del CFE (el borrador se aplica después en el useEffect de montaje)
  const [lineas, setLineas] = useState(() => {
    const neto = Number(compra.monto_neto || 0) * signo
    const ivaBasica = Number(compra.monto_iva_basica || 0) * signo
    const ivaMinima = Number(compra.monto_iva_minima || 0) * signo
    const totalCalc = neto + ivaBasica + ivaMinima
    const gastoCta = cuentaGastoCodigo(compra.cuenta_contable)
    const arr = []
    if (neto !== 0) arr.push({ cuenta_codigo: gastoCta, debe: Math.max(neto, 0), haber: Math.max(-neto, 0), detalle: 'Neto compra' })
    if (ivaBasica !== 0) arr.push({ cuenta_codigo: '1142102', debe: Math.max(ivaBasica, 0), haber: Math.max(-ivaBasica, 0), detalle: 'IVA Crédito 22%' })
    if (ivaMinima !== 0) arr.push({ cuenta_codigo: '1142101', debe: Math.max(ivaMinima, 0), haber: Math.max(-ivaMinima, 0), detalle: 'IVA Crédito 10%' })
    if (totalCalc !== 0) arr.push({ cuenta_codigo: '2112101', debe: Math.max(-totalCalc, 0), haber: Math.max(totalCalc, 0), detalle: `Deuda con ${proveedor}` })
    return arr
  })

  // Snapshot inicial (líneas sugeridas del CFE) — para comparar y NO auto-guardar si Roberto no cambió nada
  const snapshotInicial = useRef(null)
  const [modificado, setModificado] = useState(false)

  // Chequear borrador UNA vez al montar (dentro de useEffect, no en el body!)
  useEffect(() => {
    snapshotInicial.current = JSON.stringify({ cuenta, lineas })
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) {
        const b = JSON.parse(raw)
        if (Date.now() - b.timestamp < 7 * 86400000 && Array.isArray(b.lineas) && b.lineas.length > 0) {
          // Solo pregunto si el borrador es DIFERENTE del estado inicial (sino no aporta nada)
          const borradorStr = JSON.stringify({ cuenta: b.cuenta, lineas: b.lineas })
          if (borradorStr !== snapshotInicial.current) {
            const restaurar = window.confirm(
              `Hay un borrador de esta compra del ${new Date(b.timestamp).toLocaleString('es-UY')}.\n\n¿Restaurar? (Cancelar = descartar el borrador y arrancar de las líneas sugeridas del CFE)`
            )
            if (restaurar) {
              setCuenta(b.cuenta)
              setLineas(b.lineas)
              setModificado(true)
            } else {
              localStorage.removeItem(storageKey)
            }
          } else {
            // Borrador idéntico al inicial → lo borro sin molestar
            localStorage.removeItem(storageKey)
          }
        }
      }
    } catch {}
    setDecidido(true)
  }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  // Detecto si Roberto cambió algo respecto al inicial (para escribir borrador solo si vale la pena)
  useEffect(() => {
    if (!decidido || !snapshotInicial.current) return
    const actual = JSON.stringify({ cuenta, lineas })
    if (actual !== snapshotInicial.current) setModificado(true)
  }, [cuenta, lineas, decidido])

  // Auto-save debounce 300ms — SOLO si Roberto ya modificó algo respecto al inicial
  useEffect(() => {
    if (!decidido || !modificado) return
    const t = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify({ cuenta, lineas, timestamp: Date.now() }))
        setAutoSaved(new Date())
      } catch {}
    }, 300)
    return () => clearTimeout(t)
  }, [cuenta, lineas, storageKey, decidido, modificado])

  // Cargar plan de cuentas Zeta oficial (368 cuentas, 228 imputables activas)
  useEffect(() => {
    supabase.from('cuentas_contables')
      .select('codigo, nombre, tipo, es_imputable')
      .eq('activa', true)
      .eq('es_imputable', true)
      .order('codigo')
      .then(({ data }) => {
        // Normalizamos a la forma { codigo_cuenta, cuenta_nombre } usada por el select
        setPlanCuentas((data || []).map(c => ({
          codigo_cuenta: c.codigo,
          cuenta_nombre: c.nombre,
          tipo: c.tipo,
        })))
      })
  }, [])

  function nombreCuenta(codigo) {
    return planCuentas.find(p => p.codigo_cuenta === codigo)?.cuenta_nombre || '—'
  }

  function updateLinea(idx, campo, valor) {
    setLineas(prev => prev.map((l, i) => i === idx ? { ...l, [campo]: valor } : l))
  }
  function agregarLinea() {
    setLineas(prev => [...prev, { cuenta_codigo: '511204', debe: 0, haber: 0, detalle: '' }])
  }
  function eliminarLinea(idx) {
    setLineas(prev => prev.filter((_, i) => i !== idx))
  }

  const totalDebe = lineas.reduce((s, l) => s + Number(l.debe || 0), 0)
  const totalHaber = lineas.reduce((s, l) => s + Number(l.haber || 0), 0)
  const cuadrado = Math.abs(totalDebe - totalHaber) < 0.01

  async function aprobar() {
    if (!cuadrado) { alert('El asiento no cuadra. Revisá los montos DEBE y HABER.'); return }
    if (lineas.length < 2) { alert('El asiento necesita al menos 2 líneas (partida doble).'); return }
    for (const l of lineas) {
      if (!l.cuenta_codigo) { alert('Todas las líneas deben tener cuenta.'); return }
    }
    setAprobando(true)
    const { data, error } = await supabase.rpc('compra_aprobar_con_asiento_editado', {
      p_compra_id: compra.id,
      p_cuenta_contable_texto: cuenta,
      p_lineas: lineas.map(l => ({
        cuenta_codigo: l.cuenta_codigo,
        debe: Number(l.debe || 0),
        haber: Number(l.haber || 0),
        detalle: l.detalle || '',
      })),
    })
    setAprobando(false)
    if (error) { alert('Error: ' + error.message); return }
    localStorage.removeItem(storageKey)  // Limpio borrador ya persistido en BD
    onAprobado()
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, maxWidth: 960, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: '#1A3461' }}>✏️ Editor de asiento contable</h2>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
              {esNC ? 'NC ' : ''}{compra.serie}-{compra.numero} · {proveedor} · Total factura ${Number(compra.monto_total).toLocaleString('es-UY')}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>

        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 6 }}>
            Categoría de gasto (para reportes internos)
          </label>
          <select value={cuenta} onChange={(e) => setCuenta(e.target.value)}
            style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1.5px solid #cbd5e1', fontSize: 13, background: '#fff' }}>
            {CUENTAS_CONTABLES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
          <thead style={{ background: '#f1f5f9' }}>
            <tr>
              <th style={{ padding: 6, textAlign: 'left', fontSize: 10, textTransform: 'uppercase', width: 240 }}>Cuenta</th>
              <th style={{ padding: 6, textAlign: 'left', fontSize: 10, textTransform: 'uppercase' }}>Detalle</th>
              <th style={{ padding: 6, textAlign: 'right', fontSize: 10, textTransform: 'uppercase', width: 110 }}>Debe</th>
              <th style={{ padding: 6, textAlign: 'right', fontSize: 10, textTransform: 'uppercase', width: 110 }}>Haber</th>
              <th style={{ padding: 6, width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {lineas.map((l, i) => (
              <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: 4 }}>
                  <select value={l.cuenta_codigo} onChange={(e) => updateLinea(i, 'cuenta_codigo', e.target.value)}
                    style={{ width: '100%', padding: '5px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11, background: '#fff' }}>
                    {planCuentas.map(p => <option key={p.codigo_cuenta} value={p.codigo_cuenta}>{p.codigo_cuenta} · {p.cuenta_nombre}</option>)}
                  </select>
                </td>
                <td style={{ padding: 4 }}>
                  <input type="text" value={l.detalle} onChange={(e) => updateLinea(i, 'detalle', e.target.value)}
                    style={{ width: '100%', padding: '5px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11 }} />
                </td>
                <td style={{ padding: 4 }}>
                  <input type="number" step="0.01" value={l.debe} onChange={(e) => updateLinea(i, 'debe', e.target.value)}
                    style={{ width: '100%', padding: '5px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#dc2626' }} />
                </td>
                <td style={{ padding: 4 }}>
                  <input type="number" step="0.01" value={l.haber} onChange={(e) => updateLinea(i, 'haber', e.target.value)}
                    style={{ width: '100%', padding: '5px 6px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 11, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#16a34a' }} />
                </td>
                <td style={{ padding: 4, textAlign: 'center' }}>
                  <button onClick={() => eliminarLinea(i)} title="Eliminar línea"
                    style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 16, cursor: 'pointer', padding: 2 }}>×</button>
                </td>
              </tr>
            ))}
            <tr style={{ borderTop: '2px solid #1A3461', fontWeight: 700, background: cuadrado ? '#f0fdf4' : '#fef2f2' }}>
              <td style={{ padding: 8 }} colSpan={2}>{cuadrado ? '✓ Cuadrado' : '⚠ Descuadre: ' + (totalDebe - totalHaber).toFixed(2)}</td>
              <td style={{ padding: 8, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>${totalDebe.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              <td style={{ padding: 8, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>${totalHaber.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <button onClick={agregarLinea}
            style={{ padding: '6px 12px', background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
            + Agregar línea
          </button>
          <div style={{ fontSize: 11, color: '#64748b' }}>
            Roberto puede agregar cuentas, cambiar montos y códigos antes de contabilizar.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
          <div style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
            {autoSaved ? (
              <>
                <span style={{ color: '#16a34a' }}>💾 Auto-guardado local</span>
                <span style={{ color: '#94a3b8' }}>{autoSaved.toLocaleTimeString('es-UY')}</span>
                <span style={{ color: '#94a3b8' }}>· Si te vas, al volver te lo restauro.</span>
              </>
            ) : (
              <span style={{ color: '#94a3b8' }}>Los cambios se auto-guardan localmente mientras editás.</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} className="btn ghost" style={{ padding: '8px 16px' }}>
              Cerrar
            </button>
            <button onClick={aprobar} disabled={aprobando || !cuadrado}
              title={!cuadrado ? 'Corregí el descuadre antes de aprobar' : 'Contabilizar este asiento'}
              style={{ padding: '8px 20px', background: cuadrado ? '#16a34a' : '#94a3b8', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: aprobando || !cuadrado ? 'not-allowed' : 'pointer', opacity: aprobando ? 0.6 : 1 }}>
              {aprobando ? 'Aprobando…' : '✓ Aprobar y contabilizar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// CARGA POR XML
// ============================================================
function CargaXml({ proveedores, onSaved }) {
  const [parsed, setParsed] = useState(null)
  const [xmlFile, setXmlFile] = useState(null)
  const [pdfFile, setPdfFile] = useState(null)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [proveedorMatch, setProveedorMatch] = useState(null)
  const [formaPago, setFormaPago] = useState('Contado')
  const [cuentaContable, setCuentaContable] = useState('Mercaderías')
  const [centroCosto, setCentroCosto] = useState('Operativo')
  const [notas, setNotas] = useState('')
  const [dragging, setDragging] = useState(false)

  async function manejarArchivo(file) {
    setError(null)
    if (file.name.toLowerCase().endsWith('.xml')) {
      try {
        const text = await file.text()
        const datos = parseXmlCfe(text)
        if (!datos.valido) throw new Error('XML CFE inválido. Faltan campos requeridos (tipo, RUT emisor, fecha)')
        setParsed(datos)
        setXmlFile(file)

        // Buscar proveedor por RUT
        const prov = await getProveedorPorRut(datos.emisor.rut)
        setProveedorMatch(prov)

        // Sugerir productos
        const itemsConSugerencia = await sugerirProductos(datos.items)
        setParsed(prev => ({ ...prev, items: itemsConSugerencia }))

        if (datos.formaPago) setFormaPago(datos.formaPago)
      } catch (e) {
        setError(e.message)
      }
    } else if (file.name.toLowerCase().endsWith('.pdf')) {
      setPdfFile(file)
    } else {
      setError('Solo XML o PDF')
    }
  }

  function onDrop(e) {
    e.preventDefault(); setDragging(false)
    Array.from(e.dataTransfer.files).forEach(manejarArchivo)
  }

  async function guardar() {
    if (!parsed) return
    setSaving(true); setError(null)
    try {
      await crearCompraDesdeXml(parsed, {
        xmlFile, pdfFile, formaPago, cuentaContable, centroCosto, notas,
      })
      onSaved()
    } catch (e) {
      setError(e.message)
      setSaving(false)
    }
  }

  return (
    <div style={{ marginTop: 18, maxWidth: 900 }}>

      {/* Drag and drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        style={{
          border: dragging ? '3px dashed #00A8B5' : '2px dashed #CBD5E1',
          borderRadius: 12,
          padding: 50,
          textAlign: 'center',
          background: dragging ? '#ECFEFF' : '#F8FAFC',
          transition: 'all .15s',
          cursor: 'pointer',
        }}
        onClick={() => document.getElementById('file-input').click()}
      >
        <div style={{ fontSize: 56, marginBottom: 12 }}>{dragging ? '📥' : '📤'}</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#1A3461', marginBottom: 6 }}>
          {parsed ? '✓ XML procesado' : 'Arrastrá el XML CFE aquí'}
        </div>
        <div style={{ fontSize: 13, color: '#64748B' }}>
          O click para seleccionar (XML obligatorio · PDF opcional)
        </div>
        <input
          id="file-input"
          type="file"
          accept=".xml,.pdf"
          multiple
          style={{ display: 'none' }}
          onChange={e => Array.from(e.target.files).forEach(manejarArchivo)}
        />
      </div>

      {error && (
        <div style={{ marginTop: 16, padding: 12, background: '#FEE2E2', color: '#991B1B', borderRadius: 8, fontSize: 13 }}>
          ⚠ {error}
        </div>
      )}

      {parsed && (
        <div style={{ marginTop: 24, background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #E2E8F0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 14, borderBottom: '1px solid #F1F5F9' }}>
            <h3 style={{ margin: 0, color: '#1A3461' }}>📋 Datos extraídos del XML</h3>
            {xmlFile && <span style={{ fontSize: 11, color: '#64748B' }}>📎 {xmlFile.name}</span>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {/* Emisor */}
            <div>
              <h4 style={h4}>Emisor (Proveedor)</h4>
              <div style={field}>
                <label style={lbl}>Razón Social</label>
                <div style={val}>{parsed.emisor.razonSocial || '—'}</div>
              </div>
              <div style={field}>
                <label style={lbl}>RUT</label>
                <div style={val}>{parsed.emisor.rut}</div>
              </div>
              <div style={field}>
                <label style={lbl}>Match en sistema</label>
                {proveedorMatch ? (
                  <div style={{ ...val, color: '#16A34A', fontWeight: 700 }}>
                    ✓ {proveedorMatch.nombre}
                  </div>
                ) : (
                  <div style={{ ...val, color: '#F59E0B', fontSize: 12 }}>
                    ⚠ No encontrado — se cargará solo con datos del XML
                  </div>
                )}
              </div>
            </div>

            {/* Comprobante */}
            <div>
              <h4 style={h4}>Comprobante CFE</h4>
              <div style={field}>
                <label style={lbl}>Tipo</label>
                <div style={val}>{parsed.comprobante.tipoCfeLabel}</div>
              </div>
              <div style={field}>
                <label style={lbl}>Serie - Número</label>
                <div style={val}>{parsed.comprobante.serie}-{parsed.comprobante.numero}</div>
              </div>
              <div style={field}>
                <label style={lbl}>Fecha emisión</label>
                <div style={val}>{parsed.comprobante.fechaEmision}</div>
              </div>
              <div style={field}>
                <label style={lbl}>CAE</label>
                <div style={{ ...val, fontFamily: 'monospace', fontSize: 11 }}>{parsed.comprobante.cae || '—'}</div>
              </div>
            </div>
          </div>

          {/* Importes */}
          <h4 style={{ ...h4, marginTop: 24 }}>Importes</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, background: '#F8FAFC', padding: 16, borderRadius: 8 }}>
            <Importe label="Neto" valor={parsed.importes.neto} />
            <Importe label="IVA 22%" valor={parsed.importes.ivaBasica} color="#0891B2" />
            <Importe label="IVA 10%" valor={parsed.importes.ivaMinima} color="#0891B2" />
            <Importe label="Exento" valor={parsed.importes.exento} />
            <Importe label="TOTAL" valor={parsed.importes.total} bold color="#1A3461" />
          </div>

          {/* Items */}
          {parsed.items.length > 0 && (
            <>
              <h4 style={{ ...h4, marginTop: 24 }}>Items ({parsed.items.length})</h4>
              <div style={{ background: '#fff', border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead style={{ background: '#F8FAFC' }}>
                    <tr>
                      <th style={th}>Descripción</th>
                      <th style={{ ...th, textAlign: 'center' }}>Cant.</th>
                      <th style={{ ...th, textAlign: 'right' }}>Precio</th>
                      <th style={{ ...th, textAlign: 'right' }}>Subtotal</th>
                      <th style={th}>Match producto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.items.map((it, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #F1F5F9' }}>
                        <td style={td}>{it.descripcion}</td>
                        <td style={{ ...td, textAlign: 'center' }}>{it.cantidad} {it.unidad}</td>
                        <td style={{ ...td, textAlign: 'right' }}>${Number(it.precio_unitario || 0).toLocaleString('es-UY')}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>${Number(it.monto_item || 0).toLocaleString('es-UY')}</td>
                        <td style={td}>
                          {it.producto_sugerido ? (
                            <span style={{ fontSize: 11, color: '#16A34A', fontWeight: 600 }}>
                              ✓ {it.producto_sugerido.sku}
                            </span>
                          ) : (
                            <span style={{ fontSize: 11, color: '#94A3B8' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Datos a confirmar */}
          <h4 style={{ ...h4, marginTop: 24 }}>Datos a confirmar</h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div style={field}>
              <label style={lbl}>Forma de pago</label>
              <select value={formaPago} onChange={e => setFormaPago(e.target.value)} style={input}>
                {FORMAS_PAGO.map(fp => <option key={fp} value={fp}>{fp}</option>)}
              </select>
            </div>
            <div style={field}>
              <label style={lbl}>Cuenta contable</label>
              <select value={cuentaContable} onChange={e => setCuentaContable(e.target.value)} style={input}>
                {CUENTAS_CONTABLES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={field}>
              <label style={lbl}>Centro de costo</label>
              <select value={centroCosto} onChange={e => setCentroCosto(e.target.value)} style={input}>
                {CENTROS_COSTO.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div style={field}>
            <label style={lbl}>Notas (opcional)</label>
            <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2}
              style={{ ...input, resize: 'vertical', minHeight: 50 }}
              placeholder="Cualquier observación..." />
          </div>

          <div style={{ marginTop: 24, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button className="btn ghost" onClick={() => { setParsed(null); setXmlFile(null); setPdfFile(null) }}>
              Cancelar
            </button>
            <button className="btn primary" onClick={guardar} disabled={saving}>
              {saving ? 'Guardando…' : '✓ Confirmar y guardar'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================
// CARGA MANUAL
// ============================================================
function CargaManual({ proveedores, onSaved }) {
  const [datos, setDatos] = useState({
    proveedor_id: '',
    emisor_rut: '',
    emisor_razon_social: '',
    tipo_cfe: 111,
    serie: 'A',
    numero: '',
    fecha_emision: new Date().toISOString().slice(0, 10),
    monto_neto: '',
    monto_iva_basica: '',
    monto_total: '',
    forma_pago: 'Contado',
    cuenta_contable: 'Mercaderías',
    centro_costo: 'Operativo',
    notas: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  function set(k, v) { setDatos(d => ({ ...d, [k]: v })) }

  function autoCalcular() {
    const neto = parseFloat(datos.monto_neto) || 0
    const iva = neto * 0.22
    set('monto_iva_basica', iva.toFixed(2))
    set('monto_total', (neto + iva).toFixed(2))
  }

  async function guardar() {
    setError(null); setSaving(true)
    try {
      const prov = proveedores.find(p => p.id === datos.proveedor_id)
      await crearCompraManual({
        ...datos,
        emisor_rut: datos.emisor_rut || prov?.rut || '',
        emisor_razon_social: datos.emisor_razon_social || prov?.nombre || '',
        monto_neto: parseFloat(datos.monto_neto) || 0,
        monto_iva_basica: parseFloat(datos.monto_iva_basica) || 0,
        monto_total: parseFloat(datos.monto_total) || 0,
      })
      onSaved()
    } catch (e) {
      setError(e.message); setSaving(false)
    }
  }

  return (
    <div style={{ marginTop: 18, maxWidth: 700, background: '#fff', padding: 24, borderRadius: 12, border: '1px solid #E2E8F0' }}>
      <h3 style={{ marginTop: 0, color: '#1A3461' }}>✏️ Carga manual de factura</h3>
      <p style={{ fontSize: 12, color: '#64748B', marginBottom: 18 }}>
        Usá esto solo si NO tenés el XML CFE. Si tenés el XML, andá a "Cargar XML/PDF" y se llena solo.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={field}>
          <label style={lbl}>Proveedor</label>
          <select value={datos.proveedor_id} onChange={e => {
            const id = e.target.value
            const p = proveedores.find(x => x.id === id)
            set('proveedor_id', id)
            if (p) {
              set('emisor_rut', p.rut || '')
              set('emisor_razon_social', p.nombre)
            }
          }} style={input}>
            <option value="">Seleccionar…</option>
            {proveedores.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
          </select>
        </div>
        <div style={field}>
          <label style={lbl}>RUT (si no está en el sistema)</label>
          <input value={datos.emisor_rut} onChange={e => set('emisor_rut', e.target.value)} style={input} />
        </div>
        <div style={field}>
          <label style={lbl}>Tipo CFE</label>
          <select value={datos.tipo_cfe} onChange={e => set('tipo_cfe', parseInt(e.target.value, 10))} style={input}>
            {TIPO_CFE_OPTIONS.map(t => <option key={t.v} value={t.v}>{t.v} — {t.label}</option>)}
          </select>
        </div>
        <div style={field}>
          <label style={lbl}>Serie</label>
          <input value={datos.serie} onChange={e => set('serie', e.target.value)} style={input} />
        </div>
        <div style={field}>
          <label style={lbl}>N° factura</label>
          <input type="number" value={datos.numero} onChange={e => set('numero', e.target.value)} style={input} />
        </div>
        <div style={field}>
          <label style={lbl}>Fecha emisión</label>
          <input type="date" value={datos.fecha_emision} onChange={e => set('fecha_emision', e.target.value)} style={input} />
        </div>
        <div style={field}>
          <label style={lbl}>Importe neto (s/IVA)</label>
          <input type="number" step="0.01" value={datos.monto_neto}
            onChange={e => set('monto_neto', e.target.value)}
            onBlur={autoCalcular} style={input} />
        </div>
        <div style={field}>
          <label style={lbl}>IVA 22%</label>
          <input type="number" step="0.01" value={datos.monto_iva_basica}
            onChange={e => set('monto_iva_basica', e.target.value)} style={input} />
        </div>
        <div style={field}>
          <label style={lbl}>Total c/IVA</label>
          <input type="number" step="0.01" value={datos.monto_total}
            onChange={e => set('monto_total', e.target.value)} style={input} />
        </div>
        <div style={field}>
          <label style={lbl}>Forma de pago</label>
          <select value={datos.forma_pago} onChange={e => set('forma_pago', e.target.value)} style={input}>
            {FORMAS_PAGO.map(fp => <option key={fp} value={fp}>{fp}</option>)}
          </select>
        </div>
        <div style={field}>
          <label style={lbl}>Cuenta contable</label>
          <select value={datos.cuenta_contable} onChange={e => set('cuenta_contable', e.target.value)} style={input}>
            {CUENTAS_CONTABLES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={field}>
          <label style={lbl}>Centro de costo</label>
          <select value={datos.centro_costo} onChange={e => set('centro_costo', e.target.value)} style={input}>
            {CENTROS_COSTO.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div style={field}>
        <label style={lbl}>Notas</label>
        <textarea value={datos.notas} onChange={e => set('notas', e.target.value)} rows={2}
          style={{ ...input, resize: 'vertical', minHeight: 50 }} />
      </div>

      {error && <div style={{ padding: 10, background: '#FEE2E2', color: '#991B1B', borderRadius: 6, marginTop: 12, fontSize: 13 }}>⚠ {error}</div>}

      <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="btn primary" onClick={guardar} disabled={saving}>
          {saving ? 'Guardando…' : '✓ Guardar'}
        </button>
      </div>
    </div>
  )
}

// ============================================================
// Componentes auxiliares
// ============================================================
function MenuAcciones({ compra: c, onPreview, onRecargar }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const acciones = []
  if (c.estado === 'pendiente_revision' && !c.asiento_id) {
    acciones.push({ label: '👁 Ver asiento y aprobar', color: '#166534', bg: '#dcfce7', onClick: () => { setOpen(false); onPreview() } })
  }
  if (c.asiento_id) {
    acciones.push({ label: '📊 Ver asiento', color: '#1e40af', bg: '#dbeafe', href: `/admin/finanzas/asientos?id=${c.asiento_id}` })
    acciones.push({ label: '⤺ Extornar', color: '#92400e', bg: '#fef3c7', onClick: async () => {
      setOpen(false)
      const motivo = prompt(`Extornar el asiento de ${c.serie}-${c.numero} (${c.emisor_razon_social || 'RUT ' + c.emisor_rut})?\n\nMotivo (queda registrado):`, 'Reclasificación de cuentas contables')
      if (!motivo) return
      const { error } = await supabase.rpc('extornar_asiento', { p_asiento_id: c.asiento_id, p_motivo: motivo })
      if (error) { alert('Error: ' + error.message); return }
      onRecargar()
    }})
  }
  if (c.estado === 'pendiente' || c.estado === 'aprobada') {
    acciones.push({ label: '💸 Marcar pagada', color: '#92400e', bg: '#fef3c7', onClick: async () => {
      setOpen(false)
      const fecha = prompt('Fecha en que se pagó (YYYY-MM-DD):', new Date().toISOString().slice(0,10))
      if (!fecha) return
      const fmaPago = prompt('¿Cómo se pagó? (Contado, Transferencia, Débito, Crédito, Cheque)', c.forma_pago || 'Transferencia')
      if (!fmaPago) return
      await marcarComoPagada(c.id, fecha, fmaPago)
      onRecargar()
    }})
  }
  if (!c.asiento_id) {
    acciones.push({ label: '🗑 Eliminar', color: '#991b1b', bg: '#fee2e2', separador: true, onClick: async () => {
      setOpen(false)
      if (!confirm(`¿Eliminar compra ${c.serie}-${c.numero} de ${c.emisor_razon_social || c.emisor_rut}? Esto NO se puede deshacer.`)) return
      await eliminarCompra(c.id)
      onRecargar()
    }})
  }

  if (acciones.length === 0) {
    return <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          fontSize: 12, padding: '5px 12px', background: '#fff', border: '1px solid #cbd5e1',
          color: '#334155', borderRadius: 6, fontWeight: 700, cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
        }}>
        Acciones {open ? '▴' : '▾'}
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 50,
          minWidth: 200, background: '#fff', border: '1px solid #cbd5e1',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(15,23,42,0.15)', overflow: 'hidden',
        }}>
          {acciones.map((a, i) => {
            const inner = (
              <div style={{
                padding: '9px 14px', fontSize: 12, fontWeight: 600, color: a.color, background: a.bg,
                cursor: 'pointer', whiteSpace: 'nowrap', textAlign: 'left',
                borderTop: a.separador ? '1px solid #e2e8f0' : undefined,
              }}>
                {a.label}
              </div>
            )
            if (a.href) return <a key={i} href={a.href} style={{ textDecoration: 'none', display: 'block' }}>{inner}</a>
            return <div key={i} onClick={a.onClick}>{inner}</div>
          })}
        </div>
      )}
    </div>
  )
}

function BadgeEstado({ estado }) {
  const map = {
    pendiente: { bg: '#FEF3C7', fg: '#92400E', label: 'Pendiente' },
    pagada:    { bg: '#DCFCE7', fg: '#166534', label: '✓ Pagada' },
    parcial:   { bg: '#FFEDD5', fg: '#9A3412', label: 'Parcial' },
    anulada:   { bg: '#F1F5F9', fg: '#64748B', label: 'Anulada' },
  }
  const s = map[estado] || map.pendiente
  return (
    <span style={{ background: s.bg, color: s.fg, padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700 }}>
      {s.label}
    </span>
  )
}

function Importe({ label, valor, color, bold }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 10, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{
        fontSize: bold ? 18 : 15,
        fontWeight: bold ? 800 : 600,
        color: color || '#1A1A1A',
      }}>
        ${Number(valor || 0).toLocaleString('es-UY', { minimumFractionDigits: 0 })}
      </div>
    </div>
  )
}

function tipoCfeLabel(tipo) {
  const m = { 101: 'e-Ticket', 111: 'e-Factura', 102: 'NC e-Tkt', 112: 'NC e-Fact' }
  return m[tipo] || `CFE ${tipo}`
}

const th = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }
const td = { padding: '10px 12px', fontSize: 13, color: '#1A1A1A' }
const h4 = { color: '#1A3461', fontSize: 14, marginBottom: 12, marginTop: 0 }
const field = { marginBottom: 12 }
const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.3 }
const val = { fontSize: 14, color: '#1A1A1A', fontWeight: 500 }
const input = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1.5px solid #E2E8F0', fontSize: 13, fontFamily: 'inherit', background: '#fff' }
